import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  Alert, InputAdornment, IconButton, CircularProgress, Divider,
} from '@mui/material';
import {
  Email, Lock, Visibility, VisibilityOff, AutoAwesome,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';

export default function LoginPage() {
  const { setUser } = useAuth();
  const navigate    = useNavigate();

  const [form, setForm]         = useState({ email: '', password: '' });
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const handle = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authAPI.directLogin({ email: form.email, password: form.password });
      const { accessToken, user: userData } = res.data.data;
      localStorage.setItem('token', accessToken);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(ellipse at 20% 50%, rgba(2,136,209,0.08) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(103,58,183,0.06) 0%, transparent 60%), #000000',
    }}>
      {/* Stars backdrop */}
      <Box sx={{
        position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none',
        backgroundImage: [
          'radial-gradient(1px 1px at 15% 25%, rgba(255,255,255,0.4) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 40% 70%, rgba(255,255,255,0.3) 0%, transparent 100%)',
          'radial-gradient(1.5px 1.5px at 70% 15%, rgba(255,255,255,0.5) 0%, transparent 100%)',
          'radial-gradient(1px 1px at 85% 60%, rgba(255,255,255,0.3) 0%, transparent 100%)',
        ].join(', '),
      }} />

      <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 420, px: 2 }}>
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{
            display: 'inline-flex', p: 2, borderRadius: '50%',
            bgcolor: 'rgba(2,136,209,0.15)', border: '1px solid rgba(255,255,255,0.3)', mb: 2,
          }}>
            <AutoAwesome sx={{ fontSize: 36, color: 'primary.main' }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.light', mb: 0.5 }}>
            SpacePulse
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Sign in to your account
          </Typography>
        </Box>

        <Card sx={{
          background: 'rgba(10,22,40,0.85)', backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.15)', borderRadius: 3, overflow: 'hidden',
        }}>
          {/* Top gradient accent */}
          <Box sx={{ height: 3, background: 'linear-gradient(90deg, #0288d1, #ffffff, #673ab7)' }} />

          <CardContent sx={{ p: 4 }}>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth name="email" label="Email Address" type="email"
                value={form.email} onChange={handle} required autoFocus sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: 'primary.main', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth name="password" label="Password"
                type={showPw ? 'text' : 'password'}
                value={form.password} onChange={handle} required sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: 'primary.main', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPw(v => !v)} size="small">
                        {showPw ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ textAlign: 'right', mb: 2.5 }}>
                <span
                  onClick={() => navigate('/forgot-password')}
                  style={{ color: '#ffffff', fontSize: 13, cursor: 'pointer' }}
                >
                  Forgot Password?
                </span>
              </Box>

              <Button
                type="submit" fullWidth variant="contained" size="large"
                disabled={loading}
                sx={{ py: 1.5, fontSize: 15, fontWeight: 700, borderRadius: 2, mb: 2 }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
              </Button>
            </form>

            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', px: 1 }}>OR</Typography>
            </Divider>

            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
              Don't have an account?{' '}
              <span
                onClick={() => navigate('/register')}
                style={{ color: '#ffffff', fontWeight: 600, cursor: 'pointer' }}
              >
                Create one free
              </span>
            </Typography>

            <Typography
              variant="caption"
              sx={{ display: 'block', textAlign: 'center', mt: 2, color: 'text.secondary', cursor: 'pointer' }}
              onClick={() => navigate('/')}
            >
              Continue as Guest →
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
